import { memo, useMemo } from 'react';
import { getInitialsFromName, hashString } from '@/utils/avatar';
import styles from './InitialsAvatar.module.css';

export interface InitialsAvatarProps {
  /** Full name to extract initials from */
  name: string;
  /** Photo URL - if provided, shows image instead of initials */
  photoUrl: string | null;
  /** Size variant: sm=48px, md=64px, lg=80px */
  size?: 'sm' | 'md' | 'lg';
  /** Additional CSS class */
  className?: string;
}

/**
 * 5 deterministic gradient colors from prototype.
 * Same name always produces same color via hash function.
 * UI concern: gradients kept here, pure hash logic in utils/avatar.
 */
const AVATAR_COLORS = [
  'linear-gradient(135deg, #E07A5F 0%, #C45D43 100%)', // coral (matches accent)
  'linear-gradient(135deg, #6B9E78 0%, #4A7C59 100%)', // green
  'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)', // purple
  'linear-gradient(135deg, #0EA5E9 0%, #0284C7 100%)', // blue
  'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)', // amber
];

/**
 * Get color index from name hash.
 */
function getColorIndex(name: string): number {
  return hashString(name) % AVATAR_COLORS.length;
}

/**
 * InitialsAvatar displays a profile photo or colored initials fallback.
 *
 * When photoUrl is provided, shows the image.
 * When photoUrl is null, shows initials on a deterministic colored background
 * (same name always gets the same color).
 */
function InitialsAvatarComponent({
  name,
  photoUrl,
  size = 'md',
  className = '',
}: InitialsAvatarProps) {
  const initials = useMemo(() => getInitialsFromName(name), [name]);
  const colorIndex = useMemo(() => getColorIndex(name), [name]);

  const containerClass = `${styles.avatar} ${styles[size]} ${className}`.trim();

  if (photoUrl) {
    return (
      <div className={containerClass}>
        <img src={photoUrl} alt={name} className={styles.image} />
      </div>
    );
  }

  return (
    <div
      className={`${containerClass} ${styles.initials}`}
      style={{ background: AVATAR_COLORS[colorIndex] }}
      aria-label={name}
    >
      <span className={styles.initialsText}>{initials}</span>
    </div>
  );
}

export const InitialsAvatar = memo(InitialsAvatarComponent);
